import type {
  MIServiceDiscoveryOptions, MIDiscoveredService, MIServerType, MIAuthStrategy,
} from './types';

export class MIServiceDiscovery {
  static async discover(url: string, options?: MIServiceDiscoveryOptions): Promise<MIDiscoveredService[]> {
    const baseUrl = url.replace(/\/$/, '');
    const timeout = options?.timeout || 10000;
    const services: MIDiscoveredService[] = [];
    const headers: Record<string, string> = options?.headers || {};

    if (options?.auth) {
      switch (options.auth.type) {
        case 'api-key':
          headers[options.auth.apiKeyHeader || 'X-API-Key'] = options.auth.apiKey || '';
          break;
        case 'bearer':
          headers['Authorization'] = `Bearer ${options.auth.token}`;
          break;
        case 'basic':
          headers['Authorization'] = `Basic ${btoa(`${options.auth.username}:${options.auth.password}`)}`;
          break;
      }
    }

    const probeList: { type: MIServerType; check: () => Promise<MIDiscoveredService | null> }[] = [
      {
        type: 'ogc-wms',
        check: () => MIServiceDiscovery.probeWMS(baseUrl, headers, timeout),
      },
      {
        type: 'ogc-wfs',
        check: () => MIServiceDiscovery.probeWFS(baseUrl, headers, timeout),
      },
      {
        type: 'ogc-wmts',
        check: () => MIServiceDiscovery.probeWMTS(baseUrl, headers, timeout),
      },
      {
        type: 'ogc-features',
        check: () => MIServiceDiscovery.probeOGCFeatures(baseUrl, headers, timeout),
      },
      {
        type: '3dtiles',
        check: () => MIServiceDiscovery.probe3DTiles(baseUrl, headers, timeout),
      },
      {
        type: 'arcgis-rest',
        check: () => MIServiceDiscovery.probeArcGIS(baseUrl, headers, timeout),
      },
    ];

    const filteredProbes = options?.probeServices
      ? probeList.filter(p => options.probeServices!.includes(p.type))
      : probeList;

    const results = await Promise.allSettled(
      filteredProbes.map(async (probe) => {
        const result = await probe.check();
        if (result) services.push(result);
      })
    );

    return services;
  }

  static async probe(url: string, serviceType: MIServerType): Promise<boolean> {
    try {
      const result = await MIServiceDiscovery.discover(url, {
        probeServices: [serviceType],
        timeout: 5000,
      });
      return result.length > 0;
    } catch {
      return false;
    }
  }

  private static async probeWMS(baseUrl: string, headers: Record<string, string>, timeout: number): Promise<MIDiscoveredService | null> {
    try {
      const url = `${baseUrl}?SERVICE=WMS&REQUEST=GetCapabilities&VERSION=1.3.0`;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);
      const response = await fetch(url, { headers, signal: controller.signal });
      clearTimeout(timer);
      if (!response.ok) return null;
      const text = await response.text();
      if (!text.includes('WMS_Capabilities') && !text.includes('WMT_MS_Capabilities')) return null;

      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/xml');
      const version = doc.documentElement.getAttribute('version') || '1.3.0';
      const serviceEl = doc.getElementsByTagName('Service')[0];
      const title = serviceEl?.getElementsByTagName('Title')[0]?.textContent || '';

      const layers: { name: string; title: string }[] = [];
      const layerEls = doc.getElementsByTagName('Layer');
      for (let i = 0; i < layerEls.length; i++) {
        const nameEl = layerEls[i].getElementsByTagName('Name')[0];
        const titleEl = layerEls[i].getElementsByTagName('Title')[0];
        if (nameEl?.textContent) {
          layers.push({
            name: nameEl.textContent,
            title: titleEl?.textContent || nameEl.textContent,
          });
        }
      }

      return {
        type: 'ogc-wms',
        url: baseUrl,
        version,
        title,
        layers,
        capabilities: ['GetMap', 'GetCapabilities', 'GetFeatureInfo'],
      };
    } catch {
      return null;
    }
  }

  private static async probeWFS(baseUrl: string, headers: Record<string, string>, timeout: number): Promise<MIDiscoveredService | null> {
    try {
      const url = `${baseUrl}?SERVICE=WFS&REQUEST=GetCapabilities&VERSION=2.0.0`;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);
      const response = await fetch(url, { headers, signal: controller.signal });
      clearTimeout(timer);
      if (!response.ok) return null;
      const text = await response.text();
      if (!text.includes('WFS_Capabilities') && !text.includes('wfs:WFS_Capabilities')) return null;

      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/xml');
      const version = doc.documentElement.getAttribute('version') || '2.0.0';

      const layers: { name: string; title: string }[] = [];
      const ftEls = doc.getElementsByTagName('FeatureType');
      for (let i = 0; i < ftEls.length; i++) {
        const nameEl = ftEls[i].getElementsByTagName('Name')[0];
        const titleEl = ftEls[i].getElementsByTagName('Title')[0];
        if (nameEl?.textContent) {
          layers.push({
            name: nameEl.textContent,
            title: titleEl?.textContent || nameEl.textContent,
          });
        }
      }

      return {
        type: 'ogc-wfs',
        url: baseUrl,
        version,
        layers,
        capabilities: ['GetFeature', 'DescribeFeatureType', 'GetCapabilities'],
      };
    } catch {
      return null;
    }
  }

  private static async probeWMTS(baseUrl: string, headers: Record<string, string>, timeout: number): Promise<MIDiscoveredService | null> {
    try {
      const url = `${baseUrl}?SERVICE=WMTS&REQUEST=GetCapabilities&VERSION=1.0.0`;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);
      const response = await fetch(url, { headers, signal: controller.signal });
      clearTimeout(timer);
      if (!response.ok) return null;
      const text = await response.text();
      if (!text.includes('Capabilities')) return null;

      return {
        type: 'ogc-wmts',
        url: baseUrl,
        version: '1.0.0',
        capabilities: ['GetTile', 'GetCapabilities'],
      };
    } catch {
      return null;
    }
  }

  private static async probeOGCFeatures(baseUrl: string, headers: Record<string, string>, timeout: number): Promise<MIDiscoveredService | null> {
    try {
      const url = `${baseUrl}/collections`;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);
      const response = await fetch(url, {
        headers: { ...headers, 'Accept': 'application/json' },
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!response.ok) return null;
      const data = await response.json();
      if (!data.collections) return null;

      return {
        type: 'ogc-features',
        url: baseUrl,
        layers: data.collections.map((c: any) => ({
          name: c.id,
          title: c.title || c.id,
        })),
        capabilities: ['Collections', 'Items'],
      };
    } catch {
      return null;
    }
  }

  private static async probe3DTiles(baseUrl: string, headers: Record<string, string>, timeout: number): Promise<MIDiscoveredService | null> {
    try {
      const url = `${baseUrl}/tileset.json`;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);
      const response = await fetch(url, { headers, signal: controller.signal });
      clearTimeout(timer);
      if (!response.ok) return null;
      const data = await response.json();
      if (!data.root && !data.asset) return null;

      return {
        type: '3dtiles',
        url: baseUrl,
        version: data.asset?.version || '1.0',
        capabilities: ['3DTiles', 'Tileset'],
      };
    } catch {
      return null;
    }
  }

  private static async probeArcGIS(baseUrl: string, headers: Record<string, string>, timeout: number): Promise<MIDiscoveredService | null> {
    try {
      const url = `${baseUrl}?f=json`;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);
      const response = await fetch(url, { headers, signal: controller.signal });
      clearTimeout(timer);
      if (!response.ok) return null;
      const data = await response.json();
      if (!data.currentVersion && !data.layers && !data.services) return null;

      const layers = (data.layers || data.services || []).map((l: any) => ({
        name: l.name || String(l.id),
        title: l.name || String(l.id),
      }));

      return {
        type: 'arcgis-rest',
        url: baseUrl,
        version: String(data.currentVersion || ''),
        layers,
        capabilities: ['MapServer', 'Query'],
      };
    } catch {
      return null;
    }
  }
}
